(() => {
  const ROOT_SELECTOR = '[data-hpg-training-zoom]';
  const REVEAL_SELECTOR = '[data-hpg-training-reveal]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const START_SCALE = 1.4;
  const END_SCALE = 1;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class HpgTrainingGoalsZoom {
    constructor(root) {
      this.root = root;
      this.revealItems = Array.from(root.querySelectorAll(REVEAL_SELECTOR));
      this.frameId = null;
      this.revealObserver = null;
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
    }

    connect() {
      if (this.root.dataset.hpgTrainingZoomReady === 'true') return;

      this.root.dataset.hpgTrainingZoomReady = 'true';
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
      }

      this.setupReveal();
      this.update();
    }

    disconnect() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      delete this.root.dataset.hpgTrainingZoomReady;

      if (typeof this.reducedMotion.removeEventListener === 'function') {
        this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      }

      if (this.frameId) {
        window.cancelAnimationFrame(this.frameId);
        this.frameId = null;
      }

      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }
    }

    onScroll() {
      this.requestUpdate();
    }

    onResize() {
      this.requestUpdate();
    }

    onReducedMotionChange() {
      this.setupReveal();
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.frameId) return;

      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null;
        this.update();
      });
    }

    update() {
      if (this.reducedMotion.matches) {
        this.root.style.setProperty('--hpg-training-image-scale', END_SCALE);
        return;
      }

      const rect = this.root.getBoundingClientRect();
      const progress = clamp((window.innerHeight - rect.top) / (window.innerHeight * 0.85), 0, 1);
      const scale = START_SCALE - (START_SCALE - END_SCALE) * progress;

      this.root.style.setProperty('--hpg-training-image-scale', scale.toFixed(4));
    }

    setupReveal() {
      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
        this.revealItems.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.revealItems.forEach((item) => item.classList.remove('is-visible'));

      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      }, {
        rootMargin: '0px 0px 8% 0px',
        threshold: 0.04,
      });

      this.revealItems.forEach((item) => this.revealObserver.observe(item));
    }
  }

  const instances = new WeakMap();

  const getRoots = (container) => {
    const roots = [];

    if (container.matches?.(ROOT_SELECTOR)) {
      roots.push(container);
    }

    container.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    return roots;
  };

  const setup = (container = document) => {
    getRoots(container).forEach((root) => {
      if (instances.has(root)) return;

      const instance = new HpgTrainingGoalsZoom(root);
      instances.set(root, instance);
      instance.connect();
    });
  };

  const teardown = (container = document) => {
    getRoots(container).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;

      instance.disconnect();
      instances.delete(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => setup());
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
